import { useQueryClient } from '@tanstack/react-query';
// mantine
import { LoadingOverlay } from '@mantine/core';
import { modals } from '@mantine/modals';
// icons
import { Check } from 'lucide-react';
// components
import type { IIntegration, ISetting } from '@newo/api/gateway';
import SettingsForm from '@/pages/Integrations/components/SettingsForm';
import AddConnectorForm from '@/pages/Integrations/components/AddConnectorForm';
// utils
import {
    getRequiredSettings,
    valuesToSettings
} from '@/pages/Integrations/Integrations.utils.ts';
import {
    QUERY_KEYS,
    useChangeIntegrationSettingsMutation
} from '@/services/integration/integration.queries.ts';
// assets
import styles from '@/pages/Integrations/components/AddConnector/AddConnector.module.css';

interface AddConnectorProps {
    integration: IIntegration;
    settings: ISetting[];
}

const handleAddConnector = () => {
    modals.closeAll();
};

const AddConnector = ({ integration, settings }: AddConnectorProps) => {
    const queryClient = useQueryClient();
    const {
        mutate: update,
        isPending,
        isSuccess
    } = useChangeIntegrationSettingsMutation();
    const requiredSettings = getRequiredSettings(
        integration.integration_settings_descriptions,
        settings
    );
    const handleEditSettingsSubmit = async (values: Record<string, unknown>) =>
        new Promise((resolve, reject) => {
            update(
                {
                    integrationId: integration.id,
                    data: valuesToSettings(values)
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries({
                            queryKey: QUERY_KEYS.getIntegrationSettings(
                                integration.id
                            )
                        });
                        resolve(true);
                    },
                    onError: (error) => {
                        reject(error);
                    }
                }
            );
        });

    return (
        <>
            {requiredSettings.length > 0 && (
                <div className={styles.settings}>
                    <LoadingOverlay
                        loaderProps={
                            isSuccess
                                ? {
                                      children: (
                                          <Check className={styles.checkmark} />
                                      )
                                  }
                                : { type: 'dots' }
                        }
                        visible={isPending || isSuccess}
                    />
                    <div>Required integration settings</div>
                    <SettingsForm
                        presets={requiredSettings}
                        onSubmit={handleEditSettingsSubmit}
                    />
                </div>
            )}
            <AddConnectorForm
                integration={integration}
                isDisabled={requiredSettings.length > 0 && !isSuccess}
                presets={integration.connector_settings_descriptions}
                onCancel={() => {
                    modals.closeAll();
                }}
                onSubmit={handleAddConnector}
            />
        </>
    );
};

export default AddConnector;
